import React, { useState } from "react";
import { useForm } from "react-hook-form";
import { toast } from "react-toastify";
import ButtonPrimary from '../../../Components/subcomponents/ButtonPrimary';
import BallotModal from "../../../Components/Modals/BallotModal";

export default function BallotSheet({ car_uuid, judge_uuid, goBackToDashboard }) {
  const {
    register,
    handleSubmit,
    formState: { errors },
    reset,
    getValues,
    watch,
  } = useForm();

  const currentDomain = () => {
    if (location.origin.startsWith('http://localhost')) {
      return 'http://localhost:3001';
    } else {
      return location.origin;
    }
  };

  const [showModal, setShowModal] = useState(false);
  const [formData, setFormData] = useState(null);
  const ratingValues = watch(Categories.map(category => category.name));
  const avgScore = ratingValues.length > 0 ? ratingValues.reduce((sum, value) => sum + (parseFloat(value) || 0), 0) / ratingValues.length : 0;
  const isCommentsRequired = avgScore > 21;

  const handleOpenModal = () => {
    const currentFormData = getValues();
    if (isCommentsRequired && !currentFormData.comments?.trim()) {
      // Don't open modal if comments are required but not provided
      // The error message will be shown by the form validation
      return;
    }
    setFormData(currentFormData);
    setShowModal(true);
  };

  // Sends the ballot to the server. Returns true on success so the modal can
  // advance to its confirmation page; failures are reported here via toast and
  // leave the judge on the review page with their scores intact.
  const submitBallot = async () => {
    const payload = { ...getValues(), car_uuid, judge_uuid };

    try {
      const response = await fetch(`${currentDomain(location)}/api/ballot`, {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
        },
        body: JSON.stringify(payload),
      });
      if (response.status === 409) {
        toast.error("You have already submitted a ballot for this car.");
        return false;
      }
      if (!response.ok) {
        toast.error("Failed to submit ballot. Please try again.");
        return false;
      }
      return true;
    } catch (error) {
      toast.error("Failed to submit ballot. Please try again.");
      return false;
    }
  };

  // Called when the judge closes the confirmation page after a successful submit.
  const handleComplete = () => {
    setShowModal(false);
    reset();
    goBackToDashboard();
  };


  return (
    <div className="max-w-4xl mx-auto pt-8 pb-20">
      <form onSubmit={(e) => e.preventDefault()} className="mx-8 px-8">
        <div>
          <h1 className="font-bold text-2xl pt-8">Car Show Ballot Form</h1>
        </div>
        {Categories.map((category, index) => (
          <div key={category.name} className="relative">
            <span className="absolute top-5 text-xl font-bold text-gray-600">
              {index + 1}.
            </span>
            <div className="pl-6">
              <Category
                register={register}
                errors={errors}
                {...category}
              />
            </div>
          </div>
        ))}

        <div className="flex gap-12 p-4">
          <span className="font-semibold pt-1">Comments:</span>
          <textarea
            {...register("comments", {
              maxLength: { value: 500, message: "Max length is 500 characters" },
              required: isCommentsRequired ? "Please Justify your score below" : false,
            })}
            className="border outline-none border-gray-300 w-full bg-gray-100 rounded-md resize-none px-4 py-3"
          />
          {errors.comments && (
            <span className="text-sm text-red-500">{errors.comments.message}</span>
          )}
        </div>

        <span className="flex w-full justify-end p-4">
          <ButtonPrimary label="Sign & Submit" onClick={handleSubmit(handleOpenModal)} />
        </span>
      </form>

      {showModal && (
        <BallotModal
          isOpen={showModal}
          onClose={() => setShowModal(false)}
          data={formData}
          onSubmit={submitBallot}
          onComplete={handleComplete}
        />
      )}
    </div>
  );
}


const handleInput = (e) => {
  if (e.target.value.length > 2) {
    e.target.value = e.target.value.slice(0, 2); // Only allow 2 characters
  }
};

function Category({ register, errors, name, displayName, description }) {
  const hasError = errors[name];
  return (
    <div>
      <div className="grid grid-cols-2 items-center p-4 gap-4 ">
        <span className="flex flex-col gap-2">
          <label htmlFor={name} className="font-semibold">
            {displayName}
            {hasError && (
              <span className="text-sm text-red-500 font-thin"><br />
                {["max", "min"].includes(hasError.type)
                  ? "Value must be between 0-25"
                  : "This field is required"}
              </span>
            )}
          </label>
          <span className="flex items-center gap-4 text-xl">
            <input
              type="number"
              max={25}
              onInput={handleInput}
              min={0}
              {...register(name, { required: true, min: 0, max: 25 })}
              className={`w-16 h-12 p-1 ps-4 text-2xl border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500 ${
                hasError ? "border-red-500" : "border-gray-300"
              }`}
            />
            <span className="text-2xl font-medium"> / 25</span>
          </span>
        </span>
        <span className="h-full overflow-auto">{description}</span>
      </div>
      <div className="h-px bg-black my-7 " />
    </div>
  );
}

const Categories = [
  {
    name: "paint",
    displayName: "Paint/Exterior Quality",
    description:
      "Is the bodywork well-done? Is the paint smooth and deep? Any flaws, dents, or misalignments? Glass clean and car waxed?",
  },
  {
    name: "interior",
    displayName: "Interior Quality",
    description:
      "Is the interior in excellent condition? Are seats, carpets, and headliner clean? Are any modifications professional? Check trunk/hatch and ensure no dirt or junk remains.",
  },
  {
    name: "engine",
    displayName: "Engine Compartment Quality",
    description:
      "Is the workmanship neat and high-quality? Are systems secure and harmonious? Any fluid leaks, loose wires, or exhaust issues? Is the engine compartment clean?",
  },
  {
    name: "wheels",
    displayName: "Tires/Chassis & Suspension Quality",
    description:
      "Are tires, wheels, and shocks in good condition? Modifications well done? Are brakes and wheels clean or scuffed? Do tires shine or appear worn?",
  },
  {
    name: "cleanliness",
    displayName: "Cleanliness",
    description:
      "Every part of the car should be spotless, polished, and stain-free, including the trunk, glove box, grille, doorsills, and mats.",
  },
  {
    name: "concept_integration",
    displayName: "Overall Concept and Integration",
    description:
      "Consider the car's overall quality in all categories. Does everything look well executed? Only fully finished, expertly modified cars should score high.",
  },
  {
    name: "display",
    displayName: "Display",
    description:
      "Is there a poster or documentation highlighting special systems or work in progress? Displays add points, but no display means no extra points.",
  },
];
